// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * GateTravelManager - Handles warp gate travel mechanics
 * 
 * Extracted from NavigationPanel to improve maintainability and testability.
 * Manages:
 * - Gate fuel requirement checks
 * - Universe switching coordination
 * - Destination gate lookup
 * - Gate discovery
 * - Gate travel timing
 */

import GameConfig from '../../config/GameConfig';
import { getAllLocations, getUniverseData } from '../../config/universes';

/**
 * Check if gate activation is possible
 * 
 * @param {Object} gate - Gate object with fuel requirements
 * @param {number} currentFuel - Current fuel amount
 * @param {number} maxFuel - Maximum fuel capacity
 * @returns {Object} Result with success flag and optional warning
 */
export const checkGateActivation = (gate, currentFuel, maxFuel) => {
  const fuelPercentage = (currentFuel / maxFuel) * 100;

  if (fuelPercentage < gate.fuelRequired) {
    console.warn('[GateTravelManager] Insufficient fuel for gate activation');

    return {
      success: false,
      warning: {
        type: 'WARNING',
        title: '⚠️ INSUFFICIENT FUEL FOR GATE ACTIVATION',
        message: `Warp gate requires ${gate.fuelRequired}% fuel to activate.\n\nCurrent fuel: ${fuelPercentage.toFixed(1)}%\nRequired: ${gate.fuelRequired}%\n\nRefuel at this gate before attempting activation.`,
        color: '#ff4444',
        icon: '⚠️'
      }
    };
  }

  return { success: true };
};

/**
 * Prepare gate travel by looking up destination gate
 * 
 * @param {Object} gate - Source gate object
 * @param {Function} travelThroughGate - Function to switch universes
 * @returns {Object} Result with success flag and destination gate
 */
export const prepareGateTravel = (gate, travelThroughGate) => {
  // Travel through gate (switches universe in context)
  const success = travelThroughGate(gate.id);

  if (!success) {
    console.error('[GateTravelManager] Gate travel failed');
    return { success: false };
  }

  // Look up destination gate BEFORE universe switch completes (since state updates are async)
  const destinationUniverseLocations = getAllLocations(gate.destinationUniverse);
  const destinationGate = destinationUniverseLocations.find(loc => loc.id === gate.destinationGate);

  if (!destinationGate) {
    console.error('[GateTravelManager] Destination gate not found! Gate ID:', gate.destinationGate);
    return { success: false, error: 'Destination gate not found' };
  }

  // Get the full universe object (not just the ID string)
  const destinationUniverseObj = getUniverseData(gate.destinationUniverse);

  return {
    success: true,
    destinationGate,
    destinationUniverse: destinationUniverseObj
  };
};

/**
 * Get gate travel time from config
 * 
 * @returns {number} Gate travel time in milliseconds
 */
export const getGateTravelTime = () => {
  const gateTravelTime = GameConfig.warp.gateTravelTime * 1000; // Convert to ms
  return gateTravelTime;
};

/**
 * Create location save data for gate arrival
 * 
 * @param {Object} destinationGate - Destination gate object
 * @param {string} destinationUniverse - Destination universe ID
 * @returns {Object} Location save data
 */
export const createGateArrivalSaveData = (destinationGate, destinationUniverse) => {
  return {
    universeId: destinationUniverse,
    locationId: destinationGate.id,
    // Legacy fields for backward compatibility
    id: destinationGate.id,
    name: destinationGate.name,
    distance: destinationGate.distance,
    threat: destinationGate.threat,
    resources: destinationGate.resources
  };
};

/**
 * Check if gate should be discovered
 * 
 * @param {Object} destinationGate - Destination gate object
 * @param {Set} discoveredSystems - Set of discovered system IDs
 * @returns {boolean} Whether gate should be discovered
 */
export const shouldDiscoverGate = (destinationGate, discoveredSystems) => {
  return !discoveredSystems.has(destinationGate.id);
};
